/**
 * Reading `2x + 3y <= 6`.
 *
 * ## Why a parser of its own, beside one that already exists
 *
 * `parseExpression` knows one variable and it is called `x`. That is not an oversight — it is
 * what a *function* is — and widening it to two would change what every caller means by a `Node`:
 * the sampler, the derivative, the variation table and the asymptote finder all rest on there
 * being exactly one thing to vary.
 *
 * What a miền nghiệm needs is much smaller than a general two-variable expression and it must be
 * much stricter. `x² + y ≤ 1` is a perfectly good inequality and its solution set is not a
 * half-plane; a parser that accepted it and quietly linearised it would draw a wrong region
 * confidently. So this reads exactly the shape whose answer it knows how to draw — a sum of
 * signed multiples of `x`, `y` and a constant, either side of one comparison — and refuses
 * everything else rather than approximating it.
 *
 * ## What refusing buys
 *
 * The caller reports the line. A teacher who writes `x^2 + y <= 1` is told this statement cannot
 * draw it, which is true and actionable, instead of being handed a shaded half-plane that looks
 * like an answer.
 */

/** `a·x + b·y ≤ c`, or `<` when strict. Every inequality is normalised into this one form. */
export interface HalfPlane {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  /** `<` rather than `≤`: the boundary is not part of the solution, and is drawn dashed. */
  readonly strict: boolean;
}

/** A term of a linear side: a number, or a coefficient on `x` or `y`. */
interface Linear {
  readonly x: number;
  readonly y: number;
  readonly k: number;
}

const ZERO: Linear = { x: 0, y: 0, k: 0 };

/**
 * One signed term: an optional coefficient, an optional variable, an optional divisor.
 *
 * Written out rather than tokenised because the language is this small. `2x`, `2*x`, `x`, `-y`,
 * `3`, `x/2` and `0.5y` are all of it, and anything with a power, a bracket or a second letter
 * falls through to `null` — which is the point.
 */
const TERM = /^(\d*\.?\d*)\s*\*?\s*([xy]?)\s*(?:\/\s*(\d*\.?\d+))?$/;

function readLinear(text: string): Linear | null {
  const trimmed = text.replace(/\s+/g, '');
  if (trimmed === '') return null;

  // Split before every sign that is not the first character. No brackets are allowed, so a `+`
  // or `-` is always a term boundary and never something inside a subexpression.
  const pieces = trimmed.replace(/(?!^)([+-])/g, ' $1').split(' ');

  let total = ZERO;
  for (const piece of pieces) {
    const sign = piece.startsWith('-') ? -1 : 1;
    const body = piece.replace(/^[+-]/, '');

    const match = TERM.exec(body);
    if (match === null) return null;

    const [, digits = '', variable = '', divisor] = match;
    if (digits === '' && variable === '') return null;

    const magnitude =
      (digits === '' ? 1 : Number(digits)) / (divisor === undefined ? 1 : Number(divisor));
    if (!Number.isFinite(magnitude)) return null;

    const value = sign * magnitude;
    total =
      variable === 'x'
        ? { ...total, x: total.x + value }
        : variable === 'y'
          ? { ...total, y: total.y + value }
          : { ...total, k: total.k + value };
  }

  return total;
}

/**
 * `2x + 3y <= 6`, `y > x - 1`, `x >= 0` — or `null` for anything that is not one of those.
 *
 * Both spellings of each comparison are taken: a teacher types `<=` and pastes `≤`, and being
 * told the second is not an inequality would be absurd.
 */
export function parseInequality(text: string): HalfPlane | null {
  const match = /^(.*?)(<=|>=|≤|≥|<|>)(.*)$/.exec(text);
  if (match === null) return null;

  const [, leftText = '', operator = '', rightText = ''] = match;
  const left = readLinear(leftText);
  const right = readLinear(rightText);
  if (left === null || right === null) return null;

  const strict = operator === '<' || operator === '>';
  // `≥` is `≤` with every sign turned round, so only one direction ever reaches the geometry.
  const towards = operator === '>' || operator === '>=' || operator === '≥' ? -1 : 1;

  /*
   * `x >= 0` turns into `-1·x + -0·y ≤ -0`, and negative zero is worth spending a line on. It
   * compares equal to `0` under `===` and unequal under `Object.is`, it survives into a saved
   * document as `-0`, and `Math.sign` gives it back as `-0` — so a coefficient that is really
   * absent starts behaving like one that points somewhere.
   */
  const plain = (value: number): number => (value === 0 ? 0 : value);

  const plane: HalfPlane = {
    a: plain(towards * (left.x - right.x)),
    b: plain(towards * (left.y - right.y)),
    c: plain(towards * (right.k - left.k)),
    strict,
  };

  // `3 <= 5` has no boundary and is either the whole plane or none of it. Neither is a region a
  // reader can be shown, and both are almost certainly a typing mistake.
  if (plane.a === 0 && plane.b === 0) return null;

  return plane;
}
